import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PlayerSelect } from '@/components/players/PlayerSelect';
import DatePicker from '@/components/ui/DatePicker';
import { createProgramBatch, getErrorMessage } from '@/lib/api';
import { DEFAULT_SUPPLIER, SupplierType } from '@/lib/constants/suppliers';
import { getStartOfDayInUTC } from '@/lib/date';

// Type definition for batch program creation form
interface BatchProgramFormData {
  date: Date | null;
  timeRange: {
    startMinute: string;
    endMinute: string;
  };
  supplier: string;
  playerId?: number;
}

// Type definition for validation errors
interface FormErrors {
  date?: string;
  timeRange?: {
    startMinute?: string;
    endMinute?: string;
  };
  supplier?: string;
  playerId?: string;
}

export function ProgramBatchCreate() {
  const { t } = useTranslation(['programs', 'common']);
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BatchProgramFormData>({
    date: new Date(),
    timeRange: {
      startMinute: '0',
      endMinute: '5',
    },
    supplier: DEFAULT_SUPPLIER,
    playerId: undefined,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Supplier selection change handler
  const handleSupplierChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      supplier: event.target.value,
    });
    // Clear error
    if (errors.supplier) {
      setErrors({
        ...errors,
        supplier: undefined,
      });
    }
  };

  // Player selection change handler
  const handlePlayerChange = (playerId: number | undefined) => {
    setFormData({
      ...formData,
      playerId,
    });
    // Clear error
    if (errors.playerId) {
      setErrors({
        ...errors,
        playerId: undefined,
      });
    }
  };

  // Date change handler
  const handleDateChange = (date: Date | null) => {
    setFormData({
      ...formData,
      date: date,
    });
    // Clear error
    if (errors.date) {
      setErrors({
        ...errors,
        date: undefined,
      });
    }
  };

  // Start minute change handler
  const handleStartMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numeric values
    if (value === '' || /^\d+$/.test(value)) {
      setFormData({
        ...formData,
        timeRange: {
          ...formData.timeRange,
          startMinute: value,
        },
      });
      // Clear error
      if (errors.timeRange?.startMinute) {
        setErrors({
          ...errors,
          timeRange: {
            ...errors.timeRange,
            startMinute: undefined,
          },
        });
      }
    }
  };

  // End minute change handler
  const handleEndMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numeric values
    if (value === '' || /^\d+$/.test(value)) {
      setFormData({
        ...formData,
        timeRange: {
          ...formData.timeRange,
          endMinute: value,
        },
      });
      // Clear error
      if (errors.timeRange?.endMinute) {
        setErrors({
          ...errors,
          timeRange: {
            ...errors.timeRange,
            endMinute: undefined,
          },
        });
      }
    }
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.date) {
      newErrors.date = t('programs:validation.dateRequired');
    }

    if (!formData.timeRange.startMinute) {
      newErrors.timeRange = {
        ...newErrors.timeRange,
        startMinute: t('programs:validation.startMinuteRequired'),
      };
    } else {
      const startMinute = parseInt(formData.timeRange.startMinute, 10);
      if (startMinute < 0 || startMinute > 59) {
        newErrors.timeRange = {
          ...newErrors.timeRange,
          startMinute: t('programs:validation.startMinuteRange'),
        };
      }
    }

    if (!formData.timeRange.endMinute) {
      newErrors.timeRange = {
        ...newErrors.timeRange,
        endMinute: t('programs:validation.endMinuteRequired'),
      };
    } else {
      const endMinute = parseInt(formData.timeRange.endMinute, 10);
      if (endMinute < 0 || endMinute > 59) {
        newErrors.timeRange = {
          ...newErrors.timeRange,
          endMinute: t('programs:validation.endMinuteRange'),
        };
      }
    }

    // Check relationship between start and end minutes
    if (
      formData.timeRange.startMinute &&
      formData.timeRange.endMinute &&
      !newErrors.timeRange?.startMinute &&
      !newErrors.timeRange?.endMinute
    ) {
      const startMinute = parseInt(formData.timeRange.startMinute, 10);
      const endMinute = parseInt(formData.timeRange.endMinute, 10);

      if (startMinute === endMinute && endMinute !== 0) {
        newErrors.timeRange = {
          ...newErrors.timeRange,
          endMinute: t('programs:validation.endMinuteZero'),
        };
      }
    }

    if (!formData.supplier) {
      newErrors.supplier = t('programs:validation.supplierRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 &&
           (!newErrors.timeRange || Object.keys(newErrors.timeRange).length === 0);
  };

  // Calculate duration from current time range
  const calculateDuration = (): number => {
    const startMinute = parseInt(formData.timeRange.startMinute, 10);
    const endMinute = parseInt(formData.timeRange.endMinute, 10);

    if (isNaN(startMinute) || isNaN(endMinute)) {
      return 300; // Default value (5 minutes)
    }

    if (endMinute === 0) {
      // When crossing to next hour (e.g., 55 min to 0 min of next hour)
      return (60 - startMinute) * 60; // Convert minutes to seconds
    } else if (startMinute > endMinute) {
      // When crossing to next hour (e.g., 55 min to 10 min of next hour)
      return ((60 - startMinute) + endMinute) * 60;
    } else {
      // Normal case (e.g., 10 min to 15 min)
      return (endMinute - startMinute) * 60;
    }
  };

  // Form submission handler
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const date = formData.date!;

      // Create UTC datetime string representing 00:00 in local timezone for the selected date
      const startAt = getStartOfDayInUTC(date);

      // Calculate start and end times and automatically calculate duration
      const startMinute = parseInt(formData.timeRange.startMinute, 10);
      const endMinute = parseInt(formData.timeRange.endMinute, 10);

      // Calculate duration from time range
      const durationInSeconds = calculateDuration();

      // Batch create programs using API
      const result = await createProgramBatch({
        startAt,
        timeRange: {
          startMinute,
          endMinute,
        },
        duration: durationInSeconds,
        supplier: formData.supplier,
        playerId: formData.playerId,
      });

      // When API response is successful
      setSuccess(t('programs:messages.batchCreateSuccess', { count: result.data.length }));
    } catch (err) {
      console.error('Failed to batch create programs:', err);
      // Convert error message to user-friendly format
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/programs')}
            sx={{ mr: 2 }}
            disabled={loading}
          >
            {t('common:buttons.back')}
          </Button>
          <Typography variant="h4" component="h1">
            {t('programs:titles.batchCreate')}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <DatePicker
                id="program-batch-date"
                label={t('programs:form.date')}
                value={formData.date}
                onChange={handleDateChange}
                fullWidth
              />
              {errors.date && (
                <Typography color="error" variant="caption">
                  {errors.date}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <FormControl fullWidth error={!!errors.supplier}>
                <InputLabel id="supplier-label">{t('programs:form.supplier')}</InputLabel>
                <Select
                  labelId="supplier-label"
                  value={formData.supplier}
                  label={t('programs:form.supplier')}
                  onChange={handleSupplierChange}
                >
                  <MenuItem value={SupplierType.HIVESTACK}>Hivestack</MenuItem>
                </Select>
                {errors.supplier && (
                  <FormHelperText>{errors.supplier}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <PlayerSelect
                value={formData.playerId}
                onChange={handlePlayerChange}
                error={errors.playerId}
                fullWidth
              />
            </Box>
          </Grid>

          <Grid component="div" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              {t('programs:form.timeRangeSettings')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('programs:form.timeRangeDescription')}
            </Typography>
          </Grid>

          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <TextField
                label={t('programs:form.startMinute')}
                value={formData.timeRange.startMinute}
                onChange={handleStartMinuteChange}
                fullWidth
                error={!!errors.timeRange?.startMinute}
                helperText={errors.timeRange?.startMinute}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Box>
          </Grid>

          <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
              <TextField
                label={t('programs:form.endMinute')}
                value={formData.timeRange.endMinute}
                onChange={handleEndMinuteChange}
                fullWidth
                error={!!errors.timeRange?.endMinute}
                helperText={errors.timeRange?.endMinute}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Box>
          </Grid>

          <Grid component="div" sx={{ width: '100%' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {t('programs:form.calculatedDuration', { duration: calculateDuration() })}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : t('programs:buttons.batchCreate')}
          </Button>
        </Box>
      </Paper>
    </>
  );
}
